// src/components/barista/BaristaDemo.jsx
// Demo component with mock data for testing the barista interface

import { useState, useEffect } from 'react'
import { Clock, Calendar, ChefHat, CheckCircle, AlertCircle, Truck, Utensils, Package, Printer, RefreshCw, Coffee } from 'lucide-react'
import toast from 'react-hot-toast'
import { cn } from '../../utils/cn'
import OrangeLoadingSpinner from '../ui/OrangeLoadingSpinner'

// Mock data matching API response structure
const mockOrders = [
  {
    _id: "68b0b31702ada2ee0e887e88",
    order: {
      orderNumber: "ORD12540001",
      orderDate: new Date().toISOString(),
      customer: {
        name: "Kamal Perera",
        mobileNumber: "0778278331"
      },
      deliveryProvider: "pickme"
    },
    orderType: "delivery",
    table: null,
    orderItems: [
      {
        _id: "item1",
        productId: {
          name: "Espresso Medium",
          sku: "ESP001"
        },
        quantity: 2
      },
      {
        _id: "item2", 
        productId: {
          name: "Cinnamon Roll",
          sku: "CIN001"
        },
        quantity: 4
      },
      {
        _id: "item3",
        productId: {
          name: "Blueberry Muffin", 
          sku: "BLU001"
        },
        quantity: 4
      }
    ],
    status: "pending"
  },
  {
    _id: "68b0b30602ada2ee0e887e51",
    order: {
      orderNumber: "ORD12540002",
      orderDate: new Date(Date.now() - 300000).toISOString(), // 5 mins ago
      customer: {
        name: "Sarah Fernando",
        mobileNumber: "0771234567"
      }
    },
    orderType: "take_away",
    table: null,
    orderItems: [
      {
        _id: "item4",
        productId: {
          name: "Cappuccino Large",
          sku: "CAP002"
        },
        quantity: 1
      },
      {
        _id: "item5",
        productId: {
          name: "Chocolate Croissant",
          sku: "CHC001"
        },
        quantity: 2
      }
    ],
    status: "preparing"
  },
  {
    _id: "68b0b11d0b2898aba70f8645", 
    order: {
      orderNumber: "ORD12540003",
      orderDate: new Date(Date.now() - 600000).toISOString(), // 10 mins ago
      customer: {
        name: "Michael Silva",
        mobileNumber: "0759876543"
      }
    },
    orderType: "dine_in",
    table: {
      _id: "table01",
      name: "04"
    },
    orderItems: [
      {
        _id: "item6",
        productId: {
          name: "Americano",
          sku: "AME001"
        },
        quantity: 1
      },
      {
        _id: "item7",
        productId: {
          name: "Avocado Toast",
          sku: "AVO001"
        },
        quantity: 1
      }
    ],
    status: "completed"
  },
  {
    _id: "68b0b1030b2898aba70f860d",
    order: {
      orderNumber: "ORD12540004",
      orderDate: new Date(Date.now() - 120000).toISOString(), // 2 mins ago
      customer: {
        name: "Priya Sharma",
        mobileNumber: "0762345678"
      },
      deliveryProvider: "uber"
    },
    orderType: "delivery",
    table: null,
    orderItems: [
      {
        _id: "item8",
        productId: {
          name: "Flat White",
          sku: "FLA001"
        },
        quantity: 2
      },
      {
        _id: "item9",
        productId: {
          name: "Banana Bread",
          sku: "BAN001"
        },
        quantity: 1
      }
    ],
    status: "pending"
  }
]

const BaristaDemo = ({ user }) => {
  // State management
  const [orders, setOrders] = useState(mockOrders)
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('open')
  const [currentTime, setCurrentTime] = useState(new Date())

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 60000)
    return () => clearInterval(timer)
  }, [])

  // Filter orders based on active tab
  const filteredOrders = orders.filter(order => {
    if (activeTab === 'open') {
      return ['pending', 'preparing'].includes(order.status)
    } else {
      return order.status === 'completed'
    }
  })

  // Handle status change
  const handleStatusChange = (orderId, newStatus) => {
    setOrders(prev => prev.map(order => 
      order._id === orderId 
        ? { ...order, status: newStatus }
        : order
    ))
    
    const statusText = newStatus === 'preparing' ? 'started' : 'completed'
    toast.success(`Order ${statusText} successfully`)
  }

  // Handle print
  const handlePrint = (order) => {
    console.log('Printing bill for order:', order.order.orderNumber)
    toast.success('Bill sent to printer')
  }

  // Get order type icon
  const getOrderTypeIcon = (orderType) => {
    switch (orderType) {
      case 'delivery':
        return <Truck className="h-4 w-4 text-blue-600" />
      case 'take_away':
      case 'takeaway':
        return <Package className="h-4 w-4 text-orange-600" />
      case 'dine_in':
      default:
        return <Utensils className="h-4 w-4 text-green-600" />
    }
  }

  // Get order type display name
  const getOrderTypeDisplayName = (orderType) => {
    const typeMap = {
      'dine_in': 'Dine-in',
      'take_away': 'Take-away', 
      'takeaway': 'Take-away',
      'delivery': 'Delivery'
    }
    return typeMap[orderType] || 'Unknown'
  }

  // Get status display info
  const getStatusDisplayInfo = (status) => {
    const statusMap = {
      'pending': {
        name: 'Preparing',
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200'
      },
      'preparing': {
        name: 'Preparing', 
        color: 'bg-blue-100 text-blue-800 border-blue-200'
      },
      'completed': {
        name: 'Completed',
        color: 'bg-green-100 text-green-800 border-green-200'
      }
    }
    return statusMap[status] || {
      name: 'Unknown',
      color: 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  // Get status button config
  const getStatusButtonConfig = (currentStatus) => {
    switch (currentStatus) {
      case 'pending':
        return {
          text: 'Start Preparing',
          nextStatus: 'preparing',
          className: 'bg-blue-600 hover:bg-blue-700 text-white'
        }
      case 'preparing':
        return {
          text: 'Complete Order',
          nextStatus: 'completed', 
          className: 'bg-green-600 hover:bg-green-700 text-white'
        }
      case 'completed':
        return {
          text: 'Print Bill',
          action: 'print',
          className: 'bg-red-600 hover:bg-red-700 text-white'
        }
      default:
        return null
    }
  }

  // Format date/time
  const formatDateTime = (date) => {
    if (!date) return ''
    try {
      return new Date(date).toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
    } catch {
      return ''
    }
  }

  // Calculate total items
  const calculateTotalItems = (orderItems = []) => {
    return orderItems.reduce((total, item) => total + (item.quantity || 0), 0)
  }

  // Order Card Component
  const OrderCard = ({ order }) => {
    const orderInfo = order.order
    const statusInfo = getStatusDisplayInfo(order.status)
    const buttonConfig = getStatusButtonConfig(order.status)
    const totalItems = calculateTotalItems(order.orderItems)
    const orderTime = formatDateTime(orderInfo.orderDate)
    const orderNumber = orderInfo.orderNumber?.replace('ORD', '') || order._id.slice(-6)

    return (
      <div className={cn(
        "bg-white rounded-lg border-2 shadow-lg hover:shadow-xl transition-all duration-200 h-auto",
        order.status === 'pending' && "border-yellow-300 bg-yellow-50/50",
        order.status === 'preparing' && "border-blue-300 bg-blue-50/50", 
        order.status === 'completed' && "border-green-300 bg-green-50/50"
      )}>
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <span className="text-xl font-bold text-gray-900">#{orderNumber}</span>
              <div className={cn(
                "px-3 py-1 rounded-full text-xs font-bold border-2",
                statusInfo.color
              )}>
                {statusInfo.name}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {getOrderTypeIcon(order.orderType)}
              <span className="text-sm font-semibold text-gray-700">
                {getOrderTypeDisplayName(order.orderType)}
              </span>
            </div>
          </div>
          
          {/* Order info row */}
          <div className="flex items-center justify-between text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4" />
              <span className="font-medium">{orderTime}</span>
            </div>
            {order.table && (
              <div className="flex items-center space-x-1">
                <Utensils className="h-4 w-4" />
                <span className="font-semibold">Table-{order.table.name}</span>
              </div>
            )}
            {order.orderType === 'delivery' && orderInfo.deliveryProvider && (
              <div className="flex items-center space-x-1 text-blue-600">
                <Truck className="h-4 w-4" />
                <span className="font-semibold capitalize">{orderInfo.deliveryProvider}</span>
              </div>
            )}
          </div>
        </div>

        {/* Order Items */}
        <div className="p-4 space-y-3 min-h-[120px] max-h-[200px] overflow-y-auto">
          {order.orderItems.map((item, itemIndex) => (
            <div key={item._id || itemIndex} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                  {item.quantity}
                </div>
                <div>
                  <span className="font-semibold text-gray-900 block">{item.productId.name}</span>
                  <span className="text-xs text-gray-500">{item.productId.sku}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50/80">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-700">
              {totalItems} item{totalItems !== 1 ? 's' : ''}
            </span>
            
            {buttonConfig && (
              <button
                onClick={() => {
                  if (buttonConfig.action === 'print') {
                    handlePrint(order)
                  } else {
                    handleStatusChange(order._id, buttonConfig.nextStatus)
                  }
                }}
                className={cn(
                  "px-6 py-3 rounded-lg font-bold transition-colors text-sm flex items-center space-x-2 shadow-md hover:shadow-lg",
                  buttonConfig.className
                )}
              >
                {buttonConfig.action === 'print' && <Printer className="h-4 w-4" />}
                <span>{buttonConfig.text}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  const openOrdersCount = orders.filter(o => ['pending', 'preparing'].includes(o.status)).length
  const completedOrdersCount = orders.filter(o => o.status === 'completed').length

  return (
    <div className="min-h-screen bg-orange-500 text-white">
      {/* Demo Banner */}
      <div className="bg-purple-600 p-2 text-center text-sm font-semibold">
        🧪 DEMO MODE - Using mock data for testing
      </div>

      {/* Header */}
      <div className="bg-orange-600 border-b border-orange-400 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-white/10 rounded-lg flex items-center justify-center p-2">
              <img
                src="/logo.png"
                alt="TEZLAA Logo"
                className="w-full h-full object-contain"
                onError={(e) => {
                  // Fallback to Coffee icon if logo fails to load
                  e.target.style.display = 'none'
                  e.target.nextSibling.style.display = 'flex'
                }}
              />
              <Coffee className="h-10 w-10 hidden" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Barista Screen - Demo</h1>
              <p className="text-orange-100 text-sm flex items-center space-x-4">
                <span className="flex items-center space-x-1">
                  <ChefHat className="h-4 w-4" />
                  <span>{user?.username || 'Demo User'}</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Calendar className="h-4 w-4" />
                  <span>{currentTime.toLocaleDateString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric'
                  })}</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Clock className="h-4 w-4" />
                  <span>{currentTime.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })}</span>
                </span>
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {/* Refresh Button */}
            <button
              onClick={() => {
                setLoading(true)
                setTimeout(() => setLoading(false), 500)
                toast.success('Demo data refreshed')
              }}
              disabled={loading}
              className="p-3 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh Orders"
            >
              <RefreshCw className={cn("h-5 w-5", loading && "animate-spin")} />
            </button>

            {/* Close Shift Button */}
            <button 
              onClick={() => toast.success('Shift closed (demo)')}
              className="bg-white/10 hover:bg-white/20 px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Close Shift
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 mt-6">
          <button
            onClick={() => setActiveTab('open')}
            className={cn(
              "px-6 py-3 rounded-lg font-bold transition-colors text-sm flex items-center space-x-2",
              activeTab === 'open' 
                ? "bg-white text-orange-600 shadow-md" 
                : "bg-white/10 hover:bg-white/20"
            )}
          >
            <AlertCircle className="h-4 w-4" />
            <span>Open Orders ({openOrdersCount})</span>
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={cn(
              "px-6 py-3 rounded-lg font-bold transition-colors text-sm flex items-center space-x-2",
              activeTab === 'completed' 
                ? "bg-white text-orange-600 shadow-md" 
                : "bg-white/10 hover:bg-white/20"
            )}
          >
            <CheckCircle className="h-4 w-4" />
            <span>Completed Orders ({completedOrdersCount})</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <OrangeLoadingSpinner size="xl" text="Loading orders..." />
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Coffee className="h-20 w-20 mx-auto mb-6 opacity-50" />
              <h3 className="text-2xl font-bold mb-2">No orders found</h3>
              <p className="text-orange-200 text-lg">
                {activeTab === 'open' 
                  ? 'No pending orders at the moment' 
                  : 'No completed orders found'
                }
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
            {filteredOrders.map((order) => (
              <OrderCard key={order._id} order={order} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default BaristaDemo
